import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Box, Paper, Typography, CircularProgress,
    List, ListItemButton, ListItemText, ListItemIcon, Collapse,
    Chip, IconButton, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    TextField, MenuItem, InputAdornment, Divider, FormControl, InputLabel, Select, Autocomplete, Menu
} from "@mui/material";
import {
    ExpandLess, ExpandMore, Map, LocationCity,
    Public, ChevronRight, FilterListOff, Add, MyLocation, Tune,
    DriveFileRenameOutline
} from "@mui/icons-material";
import {
    getZones, getZoneHierarchy, createZone, updateZone, renameCity,
    Zone, LocationNode
} from "../services/zoneService";
import { getAllStates, getCitiesByState } from "../services/referenceService";
import ZoneTable from "../components/ZoneTable";

// --- CONFIGURATION CONSTANTS ---

// Presets for "Quick Fill"
const ZONE_PRESETS: Record<string, any> = {
    STANDARD: { surge: 1.0, rushMult: 1.2, rushStart: 17, rushEnd: 21, extraBase: 0, extraKm: 0 },
    PREMIUM:  { surge: 1.0, rushMult: 1.6, rushStart: 17, rushEnd: 21, extraBase: 20, extraKm: 0 },
    AIRPORT:  { surge: 1.25, rushMult: 1.2, rushStart: 0,  rushEnd: 24, extraBase: 100, extraKm: 2 },
    WHOLE_CITY:{ surge: 1.0, rushMult: 1.0, rushStart: 9,  rushEnd: 11, extraBase: 0, extraKm: 0 }
};

const ZONE_TYPES = [
    { value: "STANDARD", label: "Standard (Default)" },
    { value: "PREMIUM", label: "Premium (High Demand)" },
    { value: "AIRPORT", label: "Airport" },
    { value: "WHOLE_CITY", label: "Whole City Boundary" }
];

const ZoneExplorer: React.FC = () => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Data State
    const [treeData, setTreeData] = useState<LocationNode[]>([]);
    const [zones, setZones] = useState<Zone[]>([]);
    const [expandedStates, setExpandedStates] = useState<Record<string, boolean>>({});

    const [loadingTable, setLoadingTable] = useState(false);
    const [loadingTree, setLoadingTree] = useState(true);

    // Reference Data State (For Dropdowns)
    const [refStates, setRefStates] = useState<any[]>([]);
    const [refCities, setRefCities] = useState<any[]>([]);
    const [selectedStateCode, setSelectedStateCode] = useState<string | null>(null);

    // -- Add/Edit Zone Form State --
    const [addOpen, setAddOpen] = useState(false);
    const [creating, setCreating] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editingId, setEditingId] = useState<number | null>(null);

    // -- Context Menu State --
    const [contextMenu, setContextMenu] = useState<{ mouseX: number; mouseY: number; type: 'state' | 'city'; name: string; parentState?: string } | null>(null);

    // -- Rename Modal State --
    const [renameOpen, setRenameOpen] = useState(false);
    const [renameData, setRenameData] = useState({ oldName: "", newName: "", state: "" });
    const [renaming, setRenaming] = useState(false);

    const [formData, setFormData] = useState({
        // Identity
        state: "",
        city: "",
        name: "",
        type: "STANDARD",
        // Geometry
        lat: "",
        lng: "",
        size: "0.02",
        // Pricing Config (Auto-filled by Type)
        surgeMultiplier: 1.0,
        rushHourMultiplier: 1.2,
        rushHourStart: 17,
        rushHourEnd: 21,
        extraBaseFare: 0,
        extraPerKm: 0,
        geocodeProvider: "",
        placesProvider: "",
        routeProvider: ""
    });

    const cityParam = searchParams.get('city');
    const stateParam = searchParams.get('state');

    const selectedLocation = cityParam
        ? { type: 'city' as const, name: cityParam }
        : stateParam
            ? { type: 'state' as const, name: stateParam }
            : null;

    // 1. Load Tree
    const loadTree = async () => {
        try {
            const res = await getZoneHierarchy();
            setTreeData(res.data);
            if (cityParam) {
                const parentState = res.data.find((node: any) => node.cities.includes(cityParam));
                if (parentState) setExpandedStates(prev => ({ ...prev, [parentState.state]: true }));
            } else if (stateParam) {
                setExpandedStates(prev => ({ ...prev, [stateParam]: true }));
            }
        } catch (error) { console.error(error); }
        finally { setLoadingTree(false); }
    };

    const loadRefStates = async () => {
        try {
            const res = await getAllStates();
            if (res.data.success) {
                setRefStates(res.data.states);
            }
        } catch (e) { console.error("Failed to load states", e); }
    };

    useEffect(() => {
        loadTree();
        loadRefStates();
    }, []);

    // 2. Fetch Zones
    const fetchZonesData = async () => {
        if (!selectedLocation) { setZones([]); return; }
        setLoadingTable(true);
        try {
            const params = selectedLocation.type === 'city'
                ? { city: selectedLocation.name }
                : { state: selectedLocation.name };
            const res = await getZones(params);
            setZones(res.data);
        } catch (error) { console.error(error); }
        finally { setLoadingTable(false); }
    };

    useEffect(() => { fetchZonesData(); }, [cityParam, stateParam]);

    // -- Handlers --

    // Helper to fetch cities when state changes
    const handleStateChange = async (stateCode: string | null) => {
        setSelectedStateCode(stateCode);
        setRefCities([]);

        // If user manually changed state, clear the city in form
        if (!stateCode) {
            setFormData(prev => ({ ...prev, state: "", city: "" }));
            return;
        }

        // Find name for the form
        const stateObj = refStates.find(s => s.isoCode === stateCode);
        setFormData(prev => ({ ...prev, state: stateObj ? stateObj.name : "", city: "" }));

        try {
            const res = await getCitiesByState(stateCode);
            if (res.data.success) {
                setRefCities(res.data.cities);
            }
        } catch (e) { console.error(e); }
    };

    // Shared logic to open Add Modal (used by Button and Context Menu)
    const openAddModal = async (initialStateName: string, initialCityName: string) => {
        // Try to find the ISO Code for the default state to load cities
        let defaultStateCode: string | null = null;
        if (initialStateName && refStates.length > 0) {
            const found = refStates.find(s => s.name === initialStateName);
            if (found) {
                defaultStateCode = found.isoCode;
                // Pre-fetch cities so dropdown isn't empty
                try {
                    const res = await getCitiesByState(found.isoCode);
                    setRefCities(res.data.cities);
                } catch(e) {}
            }
        } else {
            setRefCities([]);
        }

        setSelectedStateCode(defaultStateCode);

        // Reset Form with STANDARD preset
        const preset = ZONE_PRESETS["STANDARD"];
        setFormData({
            state: initialStateName,
            city: initialCityName,
            name: "",
            type: "STANDARD",
            lat: "",
            lng: "",
            size: "0.02",
            surgeMultiplier: preset.surge,
            rushHourMultiplier: preset.rushMult,
            rushHourStart: preset.rushStart,
            rushHourEnd: preset.rushEnd,
            extraBaseFare: preset.extraBase,
            extraPerKm: preset.extraKm,
            geocodeProvider: "",
            placesProvider: "",
            routeProvider: ""
        });
        setAddOpen(true);
    };

    const handleOpenAdd = () => {
        setEditMode(false);
        setEditingId(null);

        // Calculate based on current selection
        let defaultStateName = "";
        let defaultCityName = "";

        if (selectedLocation?.type === 'city') {
            defaultCityName = selectedLocation.name;
            const parent = treeData.find(n => n.cities.includes(defaultCityName));
            if (parent) defaultStateName = parent.state;
        } else if (selectedLocation?.type === 'state') {
            defaultStateName = selectedLocation.name;
        }
        openAddModal(defaultStateName, defaultCityName);
    };

    const handleOpenEdit = async (zone: Zone) => {
        setEditMode(true);
        setEditingId(zone.id);

        // For Reference Data (State/City dropdowns)
        if (zone.state && refStates.length > 0) {
            const found = refStates.find(s => s.name === zone.state);
            if (found) {
                setSelectedStateCode(found.isoCode);
                try {
                    const res = await getCitiesByState(found.isoCode);
                    setRefCities(res.data.cities);
                } catch(e) {}
            }
        }

        setFormData({
            state: zone.state || "",
            city: zone.city || "",
            name: zone.name,
            type: "CUSTOM",
            lat: "", // Empty indicates "No Change" to geometry
            lng: "",
            size: "",
            surgeMultiplier: zone.surgeMultiplier,
            rushHourMultiplier: zone.rushHourMultiplier || 1.2,
            rushHourStart: zone.rushHourStart || 17,
            rushHourEnd: zone.rushHourEnd || 21,
            extraBaseFare: 0,
            extraPerKm: 0,
            geocodeProvider: zone.geocodeProvider || "",
            placesProvider: zone.placesProvider || "",
            routeProvider: zone.routeProvider || ""
        });
        setAddOpen(true);
    };

    const handleTypeChange = (newType: string) => {
        const preset = ZONE_PRESETS[newType] || ZONE_PRESETS["STANDARD"];
        setFormData(prev => ({
            ...prev,
            type: newType,
            surgeMultiplier: preset.surge,
            rushHourMultiplier: preset.rushMult,
            rushHourStart: preset.rushStart,
            rushHourEnd: preset.rushEnd,
            extraBaseFare: preset.extraBase,
            extraPerKm: preset.extraKm
        }));
    };

    const handleCreateSubmit = async () => {
        if (!formData.name || !formData.city || !formData.state) {
            alert("Please fill all required fields (Name, City, State).");
            return;
        }

        // For Creation: Lat/Lng required. For Edit: Optional.
        if (!editMode && (!formData.lat || !formData.lng)) {
            alert("Latitude and Longitude are required for new zones.");
            return;
        }

        setCreating(true);
        try {
            if (editMode && editingId) {
                // UPDATE LOGIC: Only send Name
                // We strictly only send the name to ensure Geometry and Configs are not touched.
                await updateZone(editingId, {
                    name: formData.name
                });
            } else {
                // CREATE LOGIC
                await createZone({
                    name: formData.name,
                    city: formData.city,
                    state: formData.state,
                    lat: parseFloat(formData.lat),
                    lng: parseFloat(formData.lng),
                    size: parseFloat(formData.size),
                    // Pass Configs
                    surgeMultiplier: Number(formData.surgeMultiplier),
                    rushHourMultiplier: Number(formData.rushHourMultiplier),
                    rushHourStart: Number(formData.rushHourStart),
                    rushHourEnd: Number(formData.rushHourEnd),
                    extraBaseFare: Number(formData.extraBaseFare),
                    extraPerKm: Number(formData.extraPerKm),
                    geocodeProvider: formData.geocodeProvider || null,
                    placesProvider: formData.placesProvider || null,
                    routeProvider: formData.routeProvider || null
                });
            }

            setAddOpen(false);

            // 1. Refresh Hierarchy (in case new city was added)
            await loadTree();

            // 2. Select the location we just added to!
            // This triggers the useEffect -> fetchZonesData() automatically
            setSearchParams({ city: formData.city });
            if (editMode) fetchZonesData(); // Refresh table explicitly if params didn't change

        } catch (error: any) {
            console.error("Failed to save zone", error);
            alert("Failed to save zone: " + (error.response?.data?.message || error.message));
        } finally {
            setCreating(false);
        }
    };

    // --- CONTEXT MENU HANDLERS ---
    const handleContextMenu = (event: React.MouseEvent, type: 'state' | 'city', name: string, parentState?: string) => {
        event.preventDefault();
        event.stopPropagation();
        setContextMenu({
            mouseX: event.clientX - 2,
            mouseY: event.clientY - 4,
            type,
            name,
            parentState
        });
    };

    const handleContextClose = () => setContextMenu(null);

    const handleContextAddZone = () => {
        if (!contextMenu) return;
        if (contextMenu.type === 'state') openAddModal(contextMenu.name, "");
        if (contextMenu.type === 'city') openAddModal(contextMenu.parentState!, contextMenu.name);
        handleContextClose();
    };

    const handleContextRename = () => {
        if (!contextMenu || contextMenu.type !== 'city') return;
        setRenameData({ oldName: contextMenu.name, newName: contextMenu.name, state: contextMenu.parentState! });
        setRenameOpen(true);
        handleContextClose();
    };

    const performRename = async () => {
        setRenaming(true);
        try {
            await renameCity(renameData); // Use the service function
            setRenameOpen(false);
            await loadTree(); // Refresh tree (old city should vanish, new one appear)
            // Redirect to new city
            setSearchParams({ city: renameData.newName });
        } catch (error) {
            alert("Failed to rename city. Ensure the new name is valid.");
        } finally {
            setRenaming(false);
        }
    };

    // Navigation Handlers
    const handleToggleState = (stateName: string) => {
        setExpandedStates(prev => ({ ...prev, [stateName]: !prev[stateName] }));
    };
    const handleSelectState = (stateName: string) => {
        setSearchParams({ state: stateName });
        if (!expandedStates[stateName]) handleToggleState(stateName);
    };
    const handleSelectCity = (e: React.MouseEvent, cityName: string) => {
        e.stopPropagation();
        setSearchParams({ city: cityName });
    };

    return (
        <Box sx={{ p: 3, height: 'calc(100vh - 80px)', display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
            <Typography variant="h5" sx={{ fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Public color="primary" fontSize="large" />
                Zone Explorer
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 3, flexGrow: 1, width: '100%', overflow: 'hidden' }}>

                {/* LEFT: Tree */}
                <Paper elevation={2} sx={{ width: { xs: '100%', md: 320 }, minWidth: { md: 320 }, flexShrink: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 3, border: '1px solid #e0e0e0' }}>
                    <Box sx={{ p: 2, bgcolor: '#f5f7fa', borderBottom: '1px solid #eee' }}><Typography variant="subtitle2" color="textSecondary" fontWeight="bold">LOCATIONS</Typography></Box>
                    <Box sx={{ flexGrow: 1, overflowY: 'auto' }}>
                        {loadingTree ? <CircularProgress sx={{ m: 4 }} /> : (
                            <List component="nav" sx={{ p: 1 }}>
                                {treeData.map((node) => {
                                    const isExpanded = !!expandedStates[node.state];
                                    const isSelected = selectedLocation?.type === 'state' && selectedLocation?.name === node.state;
                                    return (
                                        <React.Fragment key={node.state}>
                                            <ListItemButton
                                                onClick={() => handleSelectState(node.state)}
                                                onContextMenu={(e) => handleContextMenu(e, 'state', node.state)}
                                                sx={{ borderRadius: 2, mb: 0.5, bgcolor: isSelected ? '#e3f2fd !important' : 'transparent', color: isSelected ? '#1565c0' : 'inherit' }}
                                            >
                                                <ListItemIcon sx={{ minWidth: 32 }}><Map fontSize="small" color={isSelected ? "primary" : "action"} /></ListItemIcon>
                                                <ListItemText primary={node.state} primaryTypographyProps={{ fontWeight: isSelected ? 700 : 500 }} />
                                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleToggleState(node.state); }}>{isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}</IconButton>
                                            </ListItemButton>
                                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                <List component="div" disablePadding sx={{ position: 'relative' }}>
                                                    <Box sx={{ position: 'absolute', left: 28, top: 0, bottom: 0, width: '2px', bgcolor: '#e0e0e0' }} />
                                                    {node.cities.map((city) => {
                                                        const isCitySelected = selectedLocation?.type === 'city' && selectedLocation?.name === city;
                                                        return (
                                                            <ListItemButton
                                                                key={city}
                                                                onClick={(e) => handleSelectCity(e, city)}
                                                                onContextMenu={(e) => handleContextMenu(e, 'city', city, node.state)}
                                                                sx={{ pl: 6, borderRadius: 2, mb: 0.5, bgcolor: isCitySelected ? '#fff3e0 !important' : 'transparent', color: isCitySelected ? '#ef6c00' : 'text.secondary' }}
                                                            >
                                                                <ListItemIcon sx={{ minWidth: 28 }}><LocationCity fontSize="small" sx={{ fontSize: 18 }} color={isCitySelected ? "warning" : "disabled"} /></ListItemIcon>
                                                                <ListItemText primary={city} primaryTypographyProps={{ fontSize: '0.9rem', fontWeight: isCitySelected ? 600 : 400 }} />
                                                                {isCitySelected && <ChevronRight fontSize="small" color="warning" />}
                                                            </ListItemButton>
                                                        );
                                                    })}
                                                </List>
                                            </Collapse>
                                        </React.Fragment>
                                    );
                                })}
                            </List>
                        )}
                    </Box>
                </Paper>

                {/* RIGHT: Table */}
                <Paper elevation={2} sx={{ flexGrow: 1, minWidth: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', borderRadius: 3, border: '1px solid #e0e0e0' }}>
                    <Box sx={{ p: 2, bgcolor: '#fff', borderBottom: '1px solid #eee', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Box>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>{selectedLocation ? `${selectedLocation.name} Zones` : "Zone List"}</Typography>
                            <Typography variant="body2" color="textSecondary">{selectedLocation ? `Showing all operational zones in ${selectedLocation.name}` : "Select a State or City"}</Typography>
                        </Box>
                        {selectedLocation && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <Chip label={`${zones.length} Zones`} color="primary" size="small" sx={{ fontWeight: 'bold' }} />
                                <Button variant="contained" startIcon={<Add />} size="small" onClick={handleOpenAdd}>Add Zone</Button>
                            </Box>
                        )}
                    </Box>
                    <Box sx={{ flexGrow: 1, overflow: 'auto', bgcolor: '#fff' }}>
                        {!selectedLocation ? <Box sx={{ p: 5, textAlign: 'center', color: 'text.disabled' }}><FilterListOff sx={{ fontSize: 60, opacity: 0.2 }} /><Typography>Select a location</Typography></Box>
                            : loadingTable ? <CircularProgress sx={{ m: 5 }} />
                                : <ZoneTable zones={zones} onDataChange={fetchZonesData} onEdit={handleOpenEdit} />}
                    </Box>
                </Paper>
            </Box>

            {/* --- CONTEXT MENU --- */}
            <Menu
                open={contextMenu !== null}
                onClose={handleContextClose}
                anchorReference="anchorPosition"
                anchorPosition={
                    contextMenu !== null
                        ? { top: contextMenu.mouseY, left: contextMenu.mouseX }
                        : undefined
                }
            >
                <MenuItem onClick={handleContextAddZone}>
                    <ListItemIcon><Add fontSize="small" /></ListItemIcon>
                    <ListItemText>Create Zone here</ListItemText>
                </MenuItem>
                {contextMenu?.type === 'city' && (
                    <MenuItem onClick={handleContextRename}>
                        <ListItemIcon><DriveFileRenameOutline fontSize="small" /></ListItemIcon>
                        <ListItemText>Rename City (Bulk)</ListItemText>
                    </MenuItem>
                )}
            </Menu>

            {/* --- RENAME MODAL --- */}
            <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} fullWidth maxWidth="sm">
                <DialogTitle>Rename City</DialogTitle>
                <DialogContent sx={{ pt: 1 }}>
                    <Typography variant="body2" color="textSecondary" sx={{ mb: 2 }}>
                        This will update the city name for <b>all zones</b> currently listed under "{renameData.oldName}".
                        <br/>
                        <span style={{color: 'orange'}}>Warning: Use the exact standard name to fix mismatches.</span>
                    </Typography>

                    {/* AUTOCOMPLETE FOR NEW NAME */}
                    <Autocomplete
                        options={refCities}
                        getOptionLabel={(option) => option.name}
                        freeSolo
                        value={refCities.find(c => c.name === renameData.newName) || (renameData.newName ? { name: renameData.newName } : null)}
                        onInputChange={(event, newInputValue) => {
                            setRenameData(prev => ({ ...prev, newName: newInputValue }));
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="New City Name" fullWidth autoFocus />
                        )}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRenameOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={performRename} color="warning" disabled={renaming}>
                        {renaming ? "Renaming..." : "Rename All"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* --- ADD/EDIT ZONE WIDGET --- */}
            <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{editMode ? "Edit Zone Configuration" : "Add New Zone"}</DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5, pt: 1 }}>

                        {/* SECTION 1: IDENTITY & GEOMETRY */}
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>

                            {/* Left Column: Identity & Geometry */}
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>

                                {/* Row A: Location (State / City) - UPDATED WITH AUTOCOMPLETE */}
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <Autocomplete
                                        options={refStates}
                                        getOptionLabel={(option) => option.name}
                                        value={refStates.find(s => s.name === formData.state) || null}
                                        disabled={editMode}
                                        onChange={(event, newValue) => {
                                            handleStateChange(newValue ? newValue.isoCode : null);
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="State" size="small" required />
                                        )}
                                        sx={{ flex: 1 }}
                                    />
                                    <Autocomplete
                                        options={refCities}
                                        getOptionLabel={(option) => option.name}
                                        value={refCities.find(c => c.name === formData.city) || (formData.city ? { name: formData.city } : null)}
                                        disabled={!selectedStateCode || editMode}
                                        onChange={(event, newValue) => {
                                            setFormData(prev => ({ ...prev, city: newValue ? newValue.name : "" }));
                                        }}
                                        renderInput={(params) => (
                                            <TextField {...params} label="City" size="small" required />
                                        )}
                                        sx={{ flex: 1 }}
                                        // Allow free solo if they really need to type a city not in DB yet (optional)
                                        freeSolo
                                    />
                                </Box>

                                {/* Row B: Name */}
                                <TextField
                                    label="Zone Name"
                                    value={formData.name}
                                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                                    size="small"
                                    fullWidth
                                    placeholder="e.g. Indiranagar, Downtown"
                                />

                                {/* Row C: Geometry Header */}
                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                    <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <MyLocation fontSize="inherit"/> GEOMETRY (CENTER POINT)
                                    </Typography>
                                    {editMode && (
                                        <Typography variant="caption" sx={{color: 'text.secondary', fontStyle: 'italic'}}>
                                            Geometry cannot be edited here to prevent corruption.
                                        </Typography>
                                    )}

                                    {/* Row D1: Latitude */}
                                    <TextField
                                        label="Latitude"
                                        type="number"
                                        value={formData.lat}
                                        disabled={editMode}
                                        onChange={(e) => setFormData({ ...formData, lat: e.target.value })}
                                        fullWidth
                                        size="small"
                                        inputProps={{ step: "0.0001" }}
                                    />

                                    {/* Row D2: Longitude */}
                                    <TextField
                                        label="Longitude"
                                        type="number"
                                        value={formData.lng}
                                        disabled={editMode}
                                        onChange={(e) => setFormData({ ...formData, lng: e.target.value })}
                                        fullWidth
                                        size="small"
                                        inputProps={{ step: "0.0001" }}
                                    />

                                    {/* Row D3: Size */}
                                    <TextField
                                        label="Size (Degrees)"
                                        type="number"
                                        value={formData.size}
                                        disabled={editMode}
                                        onChange={(e) => setFormData({ ...formData, size: e.target.value })}
                                        fullWidth
                                        size="small"
                                        helperText={formData.size ? `±${(parseFloat(formData.size || "0") * 55).toFixed(1)} km from center (Area: ~${((parseFloat(formData.size || "0") * 111) * 2).toFixed(1)} km²)` : ""}
                                        InputProps={{
                                            endAdornment: <InputAdornment position="end">°</InputAdornment>,
                                        }}
                                    />
                                </Box>
                            </Box>

                            <Divider orientation="vertical" flexItem />

                            {/* SECTION 2: CONFIGURATION (Right Column) */}
                            <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextField
                                    select
                                    label="Preset Type"
                                    value={formData.type}
                                    onChange={(e) => handleTypeChange(e.target.value)}
                                    disabled={editMode}
                                    size="small"
                                    fullWidth
                                    helperText="Auto-fills pricing params below"
                                >
                                    <MenuItem value="CUSTOM"><em>Custom / Current</em></MenuItem>
                                    {ZONE_TYPES.map((t) => <MenuItem key={t.value} value={t.value}>{t.label}</MenuItem>)}
                                </TextField>

                                <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                    <Public fontSize="inherit"/> MAP CONFIGURATION
                                </Typography>

                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                                    <FormControl size="small">
                                        <InputLabel>Geocode</InputLabel>
                                        <Select
                                            value={formData.geocodeProvider}
                                            label="Geocode"
                                            disabled={editMode}
                                            onChange={(e) => setFormData({...formData, geocodeProvider: e.target.value})}
                                        >
                                            <MenuItem value=""><em>Global</em></MenuItem>
                                            <MenuItem value="google">Google</MenuItem>
                                            <MenuItem value="osm">ZapppIt-OSM</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small">
                                        <InputLabel>Places</InputLabel>
                                        <Select
                                            value={formData.placesProvider}
                                            label="Places"
                                            disabled={editMode}
                                            onChange={(e) => setFormData({...formData, placesProvider: e.target.value})}
                                        >
                                            <MenuItem value=""><em>Global</em></MenuItem>
                                            <MenuItem value="google">Google</MenuItem>
                                            <MenuItem value="osm">ZapppIt-OSM</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <FormControl size="small">
                                        <InputLabel>Routes</InputLabel>
                                        <Select
                                            value={formData.routeProvider}
                                            label="Routes"
                                            disabled={editMode}
                                            onChange={(e) => setFormData({...formData, routeProvider: e.target.value})}
                                        >
                                            <MenuItem value=""><em>Global</em></MenuItem>
                                            <MenuItem value="google">Google</MenuItem>
                                            <MenuItem value="osm">ZapppIt-OSM</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Box>

                                <Typography variant="caption" color="primary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                                    <Tune fontSize="inherit"/> PRICING CONFIGURATION
                                </Typography>

                                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                                    <TextField label="Surge Multiplier" type="number" value={formData.surgeMultiplier} disabled={editMode} onChange={(e) => setFormData({...formData, surgeMultiplier: parseFloat(e.target.value)})} size="small" inputProps={{ step: 0.1, min: 0.1, max: 3.0 }} />
                                    <TextField label="Rush Multiplier" type="number" value={formData.rushHourMultiplier} disabled={editMode} onChange={(e) => setFormData({...formData, rushHourMultiplier: parseFloat(e.target.value)})} size="small" inputProps={{ step: 0.1, min: 1.0, max: 3.0 }} />

                                    <TextField label="Rush Start (0-24)" type="number" value={formData.rushHourStart} disabled={editMode} onChange={(e) => setFormData({...formData, rushHourStart: parseInt(e.target.value)})} size="small" />
                                    <TextField label="Rush End (0-24)" type="number" value={formData.rushHourEnd} disabled={editMode} onChange={(e) => setFormData({...formData, rushHourEnd: parseInt(e.target.value)})} size="small" />

                                    {!editMode && (
                                        <>
                                            <TextField label="Extra Base Fare" type="number" value={formData.extraBaseFare} disabled={editMode} onChange={(e) => setFormData({...formData, extraBaseFare: parseFloat(e.target.value)})} size="small" InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                                            <TextField label="Extra Per Km" type="number" value={formData.extraPerKm} disabled={editMode} onChange={(e) => setFormData({...formData, extraPerKm: parseFloat(e.target.value)})} size="small" inputProps={{ min: 0, max: 100 }} InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }} />
                                        </>
                                    )}
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>
                <DialogActions sx={{ p: 2.5 }}>
                    <Button onClick={() => setAddOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={handleCreateSubmit} disabled={creating}>
                        {creating ? "Saving..." : (editMode ? "Update Zone" : "Create Zone")}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default ZoneExplorer;